import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilePlus2, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { uid, money } from "@/lib/api";
import { PhotoPicker } from "@/components/VisitImages";
import { uploadVisitImages } from "@/lib/visitImages";
import { toast } from "sonner";

const blank = (patient) => ({
  id: uid(), patient_id: patient || "", date: new Date().toLocaleDateString("en-CA"),
  diagnosis: "", treatment: "", consultant_id: "", clinic_share: "", consultant_fee: "",
  lab_cost: "", total_charged: "", remarks: "", next_appointment: "", next_appointment_time: "10:00",
});

export default function LogVisit() {
  const { data, clinic, saveMany, showMoney } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState(blank(params.get("patient") || ""));
  const [busy, setBusy] = useState(false);
  const [nameQ, setNameQ] = useState(
    params.get("patient") ? data.patients.find((p) => p.id === params.get("patient"))?.name || "" : ""
  );
  const [newPatient, setNewPatient] = useState(null);
  const [photos, setPhotos] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selected = data.patients.find((p) => p.id === form.patient_id);

  const recentPatients = useMemo(() => {
    const lastVisit = {};
    data.visits.forEach((v) => {
      if (!v.patient_id || !v.date) return;
      if (!lastVisit[v.patient_id] || v.date > lastVisit[v.patient_id]) lastVisit[v.patient_id] = v.date;
    });
    return data.patients
      .filter((p) => lastVisit[p.id])
      .sort((a, b) => lastVisit[b.id].localeCompare(lastVisit[a.id]))
      .slice(0, 5);
  }, [data.patients, data.visits]);

  const canSave = (form.patient_id || (newPatient && newPatient.name.trim())) && form.date;

  const suggestions = useMemo(() => {
    const t = nameQ.trim().toLowerCase();
    if (!t || selected?.name?.toLowerCase() === t) return [];
    return data.patients.filter((p) => (p.name || "").toLowerCase().includes(t) || (p.phone || "").includes(t)).slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameQ, data.patients, form.patient_id]);

  const pick = (p) => {
    setForm({ ...form, patient_id: p.id });
    setNameQ(p.name);
    setNewPatient(null);
  };

  const startNew = () => {
    setForm({ ...form, patient_id: "" });
    setNewPatient({ name: nameQ, gender: "", age: "", phone: "" });
  };

  const clear = () => {
    setForm(blank(""));
    setNameQ("");
    setNewPatient(null);
    setPhotos([]);
  };

  const net = Number(form.total_charged || 0) - Number(form.consultant_fee || 0) - Number(form.lab_cost || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const items = [];
    let patientId = form.patient_id;
    if (newPatient) {
      if (!newPatient.name.trim()) return toast.error("Full Name is required");
      // Keep the same id across retries so a second tap can't create the patient twice.
      const p = { id: newPatient.id || uid(), ...newPatient, age: newPatient.age ? Number(newPatient.age) : null,
        address: "", notes: "", next_appointment: form.next_appointment || "",
        next_appointment_time: form.next_appointment ? form.next_appointment_time || "10:00" : "", consent: true };
      setNewPatient({ ...newPatient, id: p.id });
      items.push(["patients", p]); // the patient must exist before their visit
      patientId = p.id;
    }
    if (!patientId) return toast.error("Patient Name is required");
    if (!form.date) return toast.error("Date is required");

    items.push(["visits", {
      ...form, patient_id: patientId, consultant_id: form.consultant_id || null,
      clinic_share: Number(form.clinic_share || 0), consultant_fee: Number(form.consultant_fee || 0),
      lab_cost: Number(form.lab_cost || 0), total_charged: Number(form.total_charged || 0),
      next_appointment: form.next_appointment || null,
      next_appointment_time: form.next_appointment ? form.next_appointment_time || "10:00" : null,
    }]);
    if (form.next_appointment && !newPatient && selected) {
      items.push(["patients", { ...selected, next_appointment: form.next_appointment,
        next_appointment_time: form.next_appointment_time || "10:00" }]);
    }
    setBusy(true);
    try { await saveMany(items); } finally { setBusy(false); }
    if (photos.length) {
      const { uploaded, failed } = await uploadVisitImages(form.id, photos);
      if (failed.length) toast.error(`Visit saved, but ${failed.length} photo${failed.length === 1 ? "" : "s"} couldn't upload (${failed[0].error}). Add ${failed.length === 1 ? "it" : "them"} from the patient's page.`);
      else toast.success(`Visit saved with ${uploaded} photo${uploaded === 1 ? "" : "s"}`);
    } else toast.success("Visit saved");
    nav(`/app/patients/${patientId}`);
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-5xl space-y-4 pb-20" data-testid="log-visit-page">
      <div>
        <h1 className="dm-page-title">
          Log <span className="font-extrabold">visit</span>
        </h1>
      </div>

      <section className="dm-panel p-4 md:p-5">
        <p className="label-xs mb-3">Patient</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="relative">
            <input data-testid="av-name" className="dm-input" placeholder="Search existing patient…" value={nameQ}
              onChange={(e) => { setNameQ(e.target.value); setForm({ ...form, patient_id: "" }); setNewPatient(null); }} />
            {suggestions.length > 0 && (
              <ul data-testid="av-sugg" className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-300 bg-white" style={{ boxShadow: "0 8px 32px rgba(0,0,0,.15)" }}>
                {suggestions.map((p) => (
                  <li key={p.id}>
                    <button type="button" data-testid={`av-sugg-${p.id}`} onClick={() => pick(p)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-slate-800 transition-colors hover:bg-slate-100">
                      <span className="truncate">{p.name}</span>
                      <span className="shrink-0 font-mono text-xs text-slate-500">ID {p.code ?? "—"}{p.phone ? ` · ${p.phone}` : ""}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {nameQ.trim() && !form.patient_id && !newPatient && (
              <button type="button" data-testid="av-new-patient" onClick={startNew} className="mt-2 text-sm font-semibold dm-accent-text">
                + New patient — add "{nameQ.trim()}"
              </button>
            )}
            {!nameQ.trim() && !form.patient_id && !newPatient && recentPatients.length > 0 && (
              <div className="mt-3" data-testid="av-recent">
                <p className="mb-1.5 text-xs text-slate-500">Recent patients</p>
                <div className="flex flex-wrap gap-1.5">
                  {recentPatients.map((p) => (
                    <button key={p.id} type="button" data-testid={`av-recent-${p.id}`} onClick={() => pick(p)} className="dm-chip">
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {selected && !newPatient && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5" data-testid="av-selected-summary">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{selected.name}</p>
                <p className="truncate text-xs text-slate-500">
                  {selected.age ? `${selected.age}y` : "Age —"} · {selected.gender || "—"} · {selected.phone || "No phone"}
                </p>
              </div>
              <button type="button" data-testid="av-change-patient" onClick={() => { setNameQ(""); setForm({ ...form, patient_id: "" }); }}
                className="dm-btn-quiet !px-2 !py-1 shrink-0 text-xs">
                <X size={13} /> Change
              </button>
            </div>
          )}

          {newPatient && (
            <div className="grid gap-3 sm:grid-cols-3" data-testid="np-card">
              <p className="label-xs sm:col-span-3 flex items-center justify-between">
                New patient
                <button type="button" onClick={() => setNewPatient(null)} className="dm-btn-quiet !px-2 !py-0.5 text-xs normal-case tracking-normal">Cancel</button>
              </p>
              <div className="sm:col-span-3">
                <input data-testid="np-name" className="dm-input" placeholder="Full name *" value={newPatient.name} onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })} />
              </div>
              <select data-testid="np-gender" className="dm-input" value={newPatient.gender} onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })}>
                <option value="">Gender —</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
              <input type="number" data-testid="np-age" className="dm-input" placeholder="Age" value={newPatient.age} onChange={(e) => setNewPatient({ ...newPatient, age: e.target.value })} />
              <input data-testid="np-phone" className="dm-input font-mono" placeholder="Phone number" value={newPatient.phone} onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })} />
            </div>
          )}
        </div>
      </section>

      <section className="dm-panel p-4 md:p-5">
        <p className="label-xs mb-3">Visit details</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="label-xs mb-1.5">Date *</p>
            <input type="date" data-testid="av-date" className="dm-input" value={form.date} onChange={set("date")} required />
          </div>
          <div>
            <p className="label-xs mb-1.5">Treatment</p>
            <select data-testid="av-treatment" className="dm-input" value={form.treatment} onChange={set("treatment")}>
              <option value="">— Select treatment —</option>
              {(clinic?.treatments || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <p className="label-xs mb-1.5">Diagnosis / Tooth</p>
            <input data-testid="av-diagnosis" className="dm-input" placeholder="e.g. #16 — Dental caries" value={form.diagnosis} onChange={set("diagnosis")} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Consultant <span className="text-slate-400">(optional)</span></p>
            <select data-testid="av-consultant" className="dm-input" value={form.consultant_id} onChange={set("consultant_id")}>
              <option value="">— None —</option>
              {data.consultants.filter((c) => !c.deleted || c.id === form.consultant_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <p className="label-xs mb-1.5">Next appointment</p>
            <div className="flex gap-2">
              <input type="date" data-testid="av-next-appointment" className="dm-input" value={form.next_appointment} onChange={set("next_appointment")} />
              {form.next_appointment && (
                <input type="time" data-testid="av-next-appointment-time" className="dm-input w-32 shrink-0" value={form.next_appointment_time} onChange={set("next_appointment_time")} />
              )}
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="label-xs mb-1.5">Remarks</p>
            <textarea rows={2} data-testid="av-remarks" className="dm-input" placeholder="Clinical notes, observations, or follow-up instructions…" value={form.remarks} onChange={set("remarks")} />
          </div>
        </div>
      </section>

      <PhotoPicker files={photos} setFiles={setPhotos} />

      {showMoney ? (
        <section className="dm-panel p-4 md:p-5" data-testid="av-financials">
          <p className="label-xs mb-3">Billing</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="label-xs mb-1.5">Clinic Share (₹)</p>
              <input type="number" data-testid="av-clinic-share" className="dm-input font-mono" placeholder="0" value={form.clinic_share} onChange={set("clinic_share")} />
            </div>
            <div>
              <p className="label-xs mb-1.5">Consultant Fee (₹)</p>
              <input type="number" data-testid="av-cons-fee" className="dm-input font-mono" placeholder="0" value={form.consultant_fee} onChange={set("consultant_fee")} />
            </div>
            <div>
              <p className="label-xs mb-1.5">Lab Cost (₹)</p>
              <input type="number" data-testid="av-lab-cost" className="dm-input font-mono" placeholder="0" value={form.lab_cost} onChange={set("lab_cost")} />
            </div>
            <div>
              <p className="label-xs mb-1.5">Total charged (₹)</p>
              <input type="number" data-testid="av-total" className="dm-input font-mono" placeholder="0" value={form.total_charged} onChange={set("total_charged")} />
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-600">
            Net after Consultant Fee &amp; Lab: <span data-testid="av-net" className="font-mono font-semibold text-slate-800">{money(net)}</span>
          </p>
        </section>
      ) : (
        <p className="text-xs text-slate-500" data-testid="av-financials-hidden">🔒 Billing fields hidden in this mode</p>
      )}

      <div className="sticky bottom-0 flex justify-end gap-2 rounded-lg border border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-xl">
        <button type="button" data-testid="av-clear" onClick={clear} className="dm-btn-ghost">Clear form</button>
        <button type="submit" data-testid="av-save" disabled={!canSave || busy} className="dm-btn-primary disabled:opacity-40">
          <FilePlus2 size={15} /> Save visit
        </button>
      </div>
    </form>
  );
}
